import { Application, Blocks } from '@/types/application';
import { Detail } from '@/types/detail';
import { Configuration, Metadata } from '@/types/metadata/metadata';
import { Arg, ReturnType, Subroutine } from '@/types/subroutine';
import { ValidationError, ValidationObject } from '@/types/validation';
import { Variables } from '@/types/variable';
import { findStartblock } from '@/util/blocks';
import { isNumber, isObject } from '@/util/typeGuards';
import { blockExists, subroutineExists } from './validation';

export const validateSubroutines = (
  blocks: Blocks,
  subroutines: Subroutine[],
  variables: Variables,
  metadata: Metadata
): ValidationError[] => {
  const errors: ValidationError[] = [];
  subroutines?.forEach((subroutine) => {
    const subErrors = validateSubroutine(blocks, subroutines, subroutine.id, variables, metadata);
    if (subErrors.length) {
      errors.push(...subErrors);
    }
  });
  return errors;
};

export const validateSubroutine = (
  blocks: Blocks,
  subroutines: Subroutine[],
  subId: number,
  variables: Variables,
  metadata: Metadata
): ValidationError[] => {
  const subroutine = subroutines.find((subroutine) => subroutine.id === subId);
  if (!subroutine) {
    return [{ severity: 'warning', message: `Subroutine cannot be found` }];
  }

  const errors: ValidationError[] = [];
  const subIdName = subroutineName(subroutine);
  if (!subroutine.label) {
    errors.push({
      severity: 'error',
      message: `Subroutine ${subIdName} is missing a label`
    });
  }
  errors.push(...validateStartblock(subroutine, subIdName, blocks, metadata));
  subroutine.nodes.forEach((nodeId) => {
    if (!blockExists(blocks, nodeId)) {
      errors.push({
        severity: 'error',
        message: `Subroutine ${subIdName} is referring to a block (${nodeId}) that doesn't exist`
      });
    }
  });

  const startblock = subroutine.startblock;
  const interfaceErrors = [
    ...validateArgs(subroutine, subIdName, variables),
    ...validateReturnTypes(subroutine, subIdName)
  ];
  errors.push(
    ...(isNumber(startblock) ? interfaceErrors.map((error) => ({ ...error, id: startblock })) : interfaceErrors)
  );

  // Errors without a block id belong to the subroutine itself, which is what the validator bar navigates to.
  return errors.map((error) => ({ ...error, subroutineId: subId }));
};

export const subroutineName = (subroutine: Subroutine): string => `${subroutine.label + ' ' || ''}(${subroutine.id})`;

// Every interface member carries a datatype, an event argument's from the metadata of the start block.
// The settings panels surface this error inline on the member's row, so the rule and wording live here.
export const validateMemberType = (
  subroutine: Subroutine,
  kind: 'argument' | 'event argument' | 'return type',
  member: Arg | ReturnType,
  index: number
): ValidationError[] => {
  if (member.type) return [];

  return [
    {
      severity: 'error',
      message: `Subroutine ${subroutineName(subroutine)} has ${kind} ${member.label || `#${index + 1}`} without a type`
    }
  ];
};

// The stored startblock is read by the engine and no longer recomputed here, so it has to agree with the blocks.
const validateStartblock = (
  subroutine: Subroutine,
  subIdName: string,
  blocks: Blocks,
  metadata: Metadata
): ValidationError[] => {
  if (subroutine.link) return [];
  if (!isNumber(subroutine.startblock)) {
    return [{ severity: 'warning', message: `Subroutine ${subIdName} is missing a startblock` }];
  }

  const startblock = findStartblock(subroutine.nodes, blocks, metadata);
  if (subroutine.startblock === startblock) return [];

  return [
    {
      severity: 'error',
      message: isNumber(startblock)
        ? `Subroutine ${subIdName} has startblock (${subroutine.startblock}) but its start block is (${startblock})`
        : `Subroutine ${subIdName} has startblock (${subroutine.startblock}) but contains no start block`
    }
  ];
};

// Return type ids are unique within the subroutine's return types, and nothing else mints them.
const validateReturnTypes = (subroutine: Subroutine, subIdName: string): ValidationError[] => {
  const seenIds = new Set<number>();

  return subroutine.return_types.flatMap((returnType, index) => {
    const name = `return type ${returnType.label || `#${index + 1}`}`;
    const errors: ValidationError[] = [];

    if (!isNumber(returnType.id) || seenIds.has(returnType.id)) {
      errors.push({ severity: 'error', message: `Subroutine ${subIdName} has ${name} with a missing or duplicate id` });
    }
    seenIds.add(returnType.id);

    errors.push(...validateMemberType(subroutine, 'return type', returnType, index));

    return errors;
  });
};

/**
 * The interface and its bindings are two parallel lists, so they can disagree. A subroutine without a body
 * carries an interface but no bindings, so only the interface itself is checked there.
 * Ids are unique within a list, so args and event args are each counted against their own.
 */
const validateArgs = (subroutine: Subroutine, subIdName: string, variables: Variables): ValidationError[] => {
  const errors: ValidationError[] = [];
  const localVariables = variables.local[subroutine.id] ?? [];
  const hasBody = Boolean(subroutine.nodes.length);

  const validateArg = (
    arg: Arg,
    binding: string | undefined,
    kind: 'argument' | 'event argument',
    index: number,
    seenIds: Set<number>
  ) => {
    const name = `${kind} ${arg.label || `#${index + 1}`}`;
    if (!isNumber(arg.id) || seenIds.has(arg.id)) {
      errors.push({
        severity: 'error',
        message: `Subroutine ${subIdName} has ${name} with a missing or duplicate id`
      });
    }
    seenIds.add(arg.id);

    errors.push(...validateMemberType(subroutine, kind, arg, index));

    if (!hasBody) return;

    if (!binding) {
      if (kind !== 'event argument') {
        errors.push({
          severity: 'warning',
          message: `Subroutine ${subIdName} has ${name} without a variable`
        });
      }
      return;
    }

    const boundVariable = localVariables.find((localVariable) => localVariable.name === binding);
    if (!boundVariable) {
      errors.push({
        severity: 'error',
        message: `Subroutine ${subIdName} binds ${name} to a variable (${binding}) that doesn't exist`
      });
    } else if (arg.type && boundVariable.type !== arg.type) {
      errors.push({
        severity: 'error',
        message: `Subroutine ${subIdName} declares ${name} as type: ${arg.type} but its variable (${binding}) is of type: ${boundVariable.type}`
      });
    }
  };

  const bindings = subroutine.arg_bindings;
  const seenArgIds = new Set<number>();
  subroutine.args.forEach((arg, index) => validateArg(arg, bindings[index], 'argument', index, seenArgIds));
  if (subroutine.args.length !== bindings.length) {
    errors.push({
      severity: 'error',
      message: `Subroutine ${subIdName} has ${subroutine.args.length} arguments but ${bindings.length} bindings`
    });
  }

  const eventBindings = subroutine.event_arg_bindings ?? [];
  const seenEventArgIds = new Set<number>();
  subroutine.event_args?.forEach((eventArg, index) =>
    validateArg(eventArg, eventBindings[index], 'event argument', index, seenEventArgIds)
  );
  if (subroutine.event_args && subroutine.event_args.length !== eventBindings.length) {
    errors.push({
      severity: 'error',
      message: `Subroutine ${subIdName} has ${subroutine.event_args.length} event arguments but ${eventBindings.length} bindings`
    });
  }

  return errors;
};

export const subroutineUtil = (): ValidationObject<number | undefined> => {
  const validate = (
    application: Application,
    metadata: Metadata,
    linkedSubroutineId: number,
    nodeId: number,
    blockName: string,
    configuration: Configuration,
    details: Detail
  ): ValidationError[] => {
    const errors: ValidationError[] = [];
    const value = getValue(configuration, details);
    if (configuration.required && !value) {
      errors.push({
        severity: 'error',
        message: `Block ${blockName} is missing required subroutine: ${configuration.label}`,
        id: nodeId
      });
    }
    if (isNumber(value) && !subroutineExists(application.subroutines, value)) {
      errors.push({
        severity: 'error',
        message: `Block ${blockName} is referring to a subroutine (${value}) that doesn't exist`,
        id: nodeId
      });
    }

    return errors;
  };

  const getValue = (configuration: Configuration, obj: unknown): number | undefined => {
    if (!obj) return;
    if (isNumber(obj)) {
      return obj;
    } else if (isObject(obj)) {
      const prop = obj[configuration.value];
      return getValue(configuration, prop);
    }
    return;
  };

  return {
    validate: validate,
    getValue: getValue
  };
};
